import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Dropdown,
  Empty,
  Input,
  List,
  Modal,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { AuthStorage } from "utils/auth-storage";
import { ContactServices } from "apis/Services/ContactService";
import LoadingComponent from "components/loading-component";
import Text from "components/text";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import ProcoreLogoWhite from "assets/images/Procore_Logo_White.svg";
import ContactForm from "./contact-form";
import BulkEditForm from "./bulk-edit-form";
import {
  canManageContact,
  filterCompanies,
  getBulkEditDetails,
  getContactDetails,
  getContactKey,
  getContactName,
  getContactValues,
  getErrorMessage,
  getInvitationDetails,
} from "./directory-utils";
import styles from "../company-directory/directory.module.css";

const STATUS_COLORS = {
  Activated: "success",
  Pending: "processing",
  "Not Invited": "default",
};

const EMPTY_PERMISSIONS = {
  companyAssignablePermissions: [],
  projectAssignablePermissions: [],
};

const displayPermission = (value) =>
  value === "Default" ? "Apply Permission Template Later" : value || "—";

const ProjectContacts = () => {
  const { company_id: companyId, id: projectId } = useParams();
  const [messageApi, messageContext] = message.useMessage();
  const [modalApi, modalContext] = Modal.useModal();
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState("");
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const mutationInFlight = useRef(false);
  const permissionRequest = useRef(0);
  const accessLevel = Number(directory?.accessLevel ?? 0);
  const canManage = [2, 3].includes(accessLevel);
  const canDelete = accessLevel === 3;

  const auth = useCallback(
    () => ({
      company_id: companyId,
      project_id: projectId,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    }),
    [companyId, projectId],
  );

  const loadDirectory = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError("");
    try {
      const response = await ContactServices.getProjectsContactPage({
        ...auth(),
        id: projectId,
      });
      if (!mounted.current || version !== loadVersion.current) return;
      setDirectory(response);
      setSelectedRowKeys([]);
    } catch (error) {
      if (!mounted.current || version !== loadVersion.current) return;
      setLoadError(
        getErrorMessage(
          error,
          "Unable to load the project directory. Please try again.",
        ),
      );
      setDirectory(null);
      setSelectedRowKeys([]);
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, [auth, projectId]);

  useEffect(() => {
    mounted.current = true;
    loadDirectory();
    return () => {
      mounted.current = false;
      loadVersion.current += 1;
      permissionRequest.current += 1;
    };
  }, [loadDirectory]);

  const loadPermissions = async () => {
    const version = ++permissionRequest.current;
    setPermissionsLoading(true);
    setPermissionsError("");
    setPermissions(EMPTY_PERMISSIONS);
    try {
      const response =
        await ContactServices.readUserSpecificCompanyAndProjectAssignablePermissionsTemplates(
          auth(),
        );
      if (!mounted.current || version !== permissionRequest.current) return;
      const nextPermissions = {
        companyAssignablePermissions:
          response?.companyAssignablePermissions || [],
        projectAssignablePermissions:
          response?.projectAssignablePermissions || [],
      };
      setPermissions(nextPermissions);
      if (
        !nextPermissions.companyAssignablePermissions.length ||
        !nextPermissions.projectAssignablePermissions.length
      ) {
        setPermissionsError(
          "No company or project permission templates are available to assign.",
        );
      }
    } catch (error) {
      if (mounted.current && version === permissionRequest.current) {
        setPermissionsError(
          getErrorMessage(
            error,
            "Unable to load permission templates. Please try again.",
          ),
        );
      }
    } finally {
      if (mounted.current && version === permissionRequest.current)
        setPermissionsLoading(false);
    }
  };

  const companyGroups = useMemo(
    () => filterCompanies(directory?.content, search),
    [directory, search],
  );

  const selectedContacts = useMemo(() => {
    const selectedKeys = new Set(selectedRowKeys);
    return filterCompanies(directory?.content).flatMap(({ company, contacts }) =>
      contacts
        .filter(
          (contact) =>
            selectedKeys.has(getContactKey(contact, company)) &&
            canManageContact(accessLevel, contact),
        )
        .map((contact) => ({
          key: getContactKey(contact, company),
          contact,
        })),
    );
  }, [directory, selectedRowKeys, accessLevel]);

  const selectedKeys = selectedContacts.map(({ key }) => key);
  const visibleKeys = new Set(
    companyGroups.flatMap(({ company, contacts }) =>
      contacts.map((contact) => getContactKey(contact, company)),
    ),
  );
  const hiddenSelectedCount = selectedKeys.filter(
    (key) => !visibleKeys.has(key),
  ).length;
  const companyOptions = useMemo(
    () =>
      Object.entries(directory?.content || {}).map(([company, contacts]) => ({
        value: company,
        label: company,
        address:
          directory?.companyAddressDict?.[company] ||
          contacts[0]?.["Company Address"] ||
          "",
      })),
    [directory],
  );
  const visibleCount = companyGroups.reduce(
    (total, group) => total + group.contacts.length,
    0,
  );
  const companyKeys = companyGroups.map(({ company }) => company);
  const activeKeys = search.trim()
    ? companyKeys
    : (expandedCompanies ?? companyKeys.slice(0, 1));

  const openCreate = () => {
    if (!canManage || loading || busyAction) return;
    setFormError("");
    setDialog({ type: "create" });
    loadPermissions();
  };

  const openEdit = (contact) => {
    if (!canManageContact(accessLevel, contact) || loading || busyAction)
      return;
    setFormError("");
    setDialog({ type: "edit", contact });
    loadPermissions();
  };

  const openBulkEdit = () => {
    if (
      !canManage ||
      loading ||
      busyAction ||
      saving ||
      !selectedContacts.length
    )
      return;
    setFormError("");
    setDialog({
      type: "bulk-edit",
      contacts: selectedContacts.map(({ contact }) => contact),
    });
    loadPermissions();
  };

  const closeDialog = () => {
    if (mutationInFlight.current) return;
    permissionRequest.current += 1;
    setDialog(null);
    setFormError("");
  };

  const submitContact = async (values) => {
    if (!canManage || loading || mutationInFlight.current || !dialog) return;
    if (dialog.contact && !canManageContact(accessLevel, dialog.contact))
      return;
    const isEditing = dialog.type === "edit";
    const currentValues = isEditing
      ? getContactValues(dialog.contact)
      : null;
    if (
      permissionsLoading ||
      permissionsError ||
      (!permissions.companyAssignablePermissions.includes(
        values.inviteeCompanyPermission,
      ) &&
        values.inviteeCompanyPermission !==
          currentValues?.inviteeCompanyPermission) ||
      (!permissions.projectAssignablePermissions.includes(
        values.inviteeProjectPermission,
      ) &&
        values.inviteeProjectPermission !==
          currentValues?.inviteeProjectPermission)
    ) {
      setFormError(
        "Select available company and project permission templates before saving.",
      );
      return;
    }

    mutationInFlight.current = true;
    setSaving(true);
    setFormError("");
    try {
      if (isEditing) {
        await ContactServices.EditContact({
          ...auth(),
          id: projectId,
          editDetails: [getContactDetails(values, dialog.contact)],
        });
      } else {
        await ContactServices.AddContact({
          ...auth(),
          ...getContactDetails(values),
        });
      }
      if (!mounted.current) return;
      setDialog(null);
      messageApi.success(isEditing ? "Contact updated." : "Contact created.");
      await loadDirectory();
    } catch (error) {
      if (mounted.current)
        setFormError(
          getErrorMessage(
            error,
            isEditing
              ? "Unable to update this contact. Please try again."
              : "Unable to create this contact. Please try again.",
          ),
        );
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const submitBulkEdit = async (values) => {
    if (loading || mutationInFlight.current || dialog?.type !== "bulk-edit")
      return;
    if (
      !dialog.contacts.length ||
      dialog.contacts.some((contact) => !canManageContact(accessLevel, contact))
    )
      return;
    if (
      !values.inviteeCompany?.trim() &&
      !values.inviteeCompanyAddress?.trim() &&
      !values.inviteeCompanyPermission &&
      !values.inviteeProjectPermission
    ) {
      setFormError("Choose at least one field to update.");
      return;
    }
    if (
      (values.inviteeCompanyPermission &&
        !permissions.companyAssignablePermissions.includes(
          values.inviteeCompanyPermission,
        )) ||
      (values.inviteeProjectPermission &&
        !permissions.projectAssignablePermissions.includes(
          values.inviteeProjectPermission,
        ))
    ) {
      setFormError("Select available permission templates before saving.");
      return;
    }

    mutationInFlight.current = true;
    setSaving(true);
    setFormError("");
    try {
      await ContactServices.EditContact({
        ...auth(),
        id: projectId,
        editDetails: getBulkEditDetails(values, dialog.contacts),
      });
      if (!mounted.current) return;
      setDialog(null);
      setSelectedRowKeys([]);
      messageApi.success(
        `${dialog.contacts.length} ${dialog.contacts.length === 1 ? "contact" : "contacts"} updated.`,
      );
      await loadDirectory();
    } catch (error) {
      if (mounted.current)
        setFormError(
          getErrorMessage(
            error,
            "Unable to update the selected contacts. Please try again.",
          ),
        );
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const runAction = async (key, action, successMessage) => {
    if (!canManage || loading || mutationInFlight.current) return;
    mutationInFlight.current = true;
    setBusyAction(key);
    try {
      await action();
      if (!mounted.current) return;
      messageApi.success(successMessage);
      await loadDirectory();
    } catch (error) {
      if (mounted.current) {
        messageApi.error(
          getErrorMessage(
            error,
            "The action could not be completed. Please try again.",
          ),
        );
      }
      throw error;
    } finally {
      mutationInFlight.current = false;
      if (mounted.current) setBusyAction("");
    }
  };

  const inviteContact = (contact, company) => {
    if (!canManageContact(accessLevel, contact) || loading || busyAction)
      return;
    const isNew = contact.Status === "Not Invited";
    modalApi.confirm({
      title: isNew ? "Send Invitation" : "Resend Invitation",
      content: `Send an invitation to ${getContactName(contact)} (${contact["Email Address"]})?`,
      okText: "Send Invitation",
      onOk: () =>
        runAction(
          getContactKey(contact, company),
          () =>
            ContactServices.resendInvitation({
              ...auth(),
              invitationDetails: [getInvitationDetails(contact)],
            }),
          "Invitation sent.",
        ),
    });
  };

  const deleteContact = (contact, company) => {
    if (
      !canDelete ||
      !canManageContact(accessLevel, contact) ||
      loading ||
      busyAction
    )
      return;
    modalApi.confirm({
      title: "Delete Contact",
      content: `Delete ${getContactName(contact)} (${contact["Email Address"]}) from this project?`,
      okText: "Delete Contact",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () =>
        runAction(
          getContactKey(contact, company),
          () =>
            ContactServices.removeContact({
              ...auth(),
              inviteeEmail: [contact["Email Address"]],
            }),
          "Contact deleted.",
        ),
    });
  };

  const bulkInvite = () => {
    if (
      !canManage ||
      loading ||
      busyAction ||
      saving ||
      !selectedContacts.length
    )
      return;
    const contacts = selectedContacts.map(({ contact }) => contact);
    modalApi.confirm({
      title: "Bulk Invite",
      content: (
        <>
          <p className={styles.paragraph}>
            Send invitations to {contacts.length} selected{" "}
            {contacts.length === 1 ? "contact" : "contacts"}?
          </p>
          <List
            size="small"
            style={{ maxHeight: 240, overflowY: "auto" }}
            dataSource={contacts}
            renderItem={(contact) => (
              <List.Item>
                <div className={styles.contactStack}>
                  <span>{getContactName(contact)}</span>
                  <span className={styles.secondary}>
                    {contact["Email Address"]}
                  </span>
                </div>
              </List.Item>
            )}
          />
        </>
      ),
      okText: "Send Invitations",
      onOk: () =>
        runAction(
          "bulk-invite",
          () =>
            ContactServices.resendInvitation({
              ...auth(),
              invitationDetails: contacts.map(getInvitationDetails),
            }),
          "Invitations sent.",
        ),
    });
  };

  const bulkDelete = () => {
    if (
      !canDelete ||
      loading ||
      busyAction ||
      saving ||
      !selectedContacts.length
    )
      return;
    const contacts = selectedContacts.map(({ contact }) => contact);
    modalApi.confirm({
      title: "Bulk Delete",
      content: `Delete ${contacts.length} selected ${contacts.length === 1 ? "contact" : "contacts"} from this project?`,
      okText: "Delete Contacts",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () =>
        runAction(
          "bulk-delete",
          () =>
            ContactServices.removeContact({
              ...auth(),
              inviteeEmail: contacts.map(
                (contact) => contact["Email Address"],
              ),
            }),
          "Contacts deleted.",
        ),
    });
  };

  const rowSelection = (company, contacts) => {
    const groupKeys = new Set(
      contacts.map((contact) => getContactKey(contact, company)),
    );
    const eligibleKeys = new Set(
      contacts
        .filter((contact) => canManageContact(accessLevel, contact))
        .map((contact) => getContactKey(contact, company)),
    );
    return {
      selectedRowKeys: selectedKeys.filter((key) => groupKeys.has(key)),
      preserveSelectedRowKeys: true,
      columnWidth: 48,
      onChange: (keys) => {
        if (!canManage || loading || busyAction || saving || dialog) return;
        setSelectedRowKeys((previous) => [
          ...previous.filter((key) => !groupKeys.has(key)),
          ...keys.filter((key) => eligibleKeys.has(key)),
        ]);
      },
      getCheckboxProps: (contact) => ({
        disabled:
          !canManageContact(accessLevel, contact) ||
          loading ||
          Boolean(busyAction) ||
          saving ||
          Boolean(dialog),
        "aria-label": `Select ${getContactName(contact)}`,
      }),
    };
  };

  const syncContacts = () => {
    if (!canManage || loading || busyAction) return;
    modalApi.confirm({
      title: "Sync Project Contacts with Procore",
      content:
        "Refresh this project directory with contacts from Procore? Previously removed users may return; no invitations will be sent.",
      okText: "Sync Contacts",
      onOk: () =>
        runAction(
          "sync",
          () => ContactServices.resyncContacts(auth()),
          "Project contacts synced.",
        ),
    });
  };

  const syncButton = (primary = false) => (
    <Button
      className={styles.syncButton}
      type={primary ? "primary" : "default"}
      icon={
        <img
          src={primary ? ProcoreLogoWhite : ProcoreLogo}
          alt=""
          width={16}
          height={16}
        />
      }
      onClick={syncContacts}
      disabled={!canManage || loading || Boolean(busyAction)}
      loading={busyAction === "sync"}
    >
      Sync with Procore
    </Button>
  );

  const columns = (company) => [
    {
      title: "Contact",
      key: "contact",
      width: 250,
      sorter: (a, b) => getContactName(a).localeCompare(getContactName(b)),
      render: (_, contact) => (
        <>
          <div className={styles.contactName}>{getContactName(contact)}</div>
          <span className={`${styles.secondary} ${styles.email}`}>
            {contact["Email Address"] || "—"}
          </span>
        </>
      ),
    },
    {
      title: "Phone",
      dataIndex: "Phone",
      width: 160,
      render: (value) => value || "—",
    },
    {
      title: "Company Address",
      dataIndex: "Company Address",
      width: 220,
      render: (value) => value || "—",
    },
    {
      title: "Company Permissions",
      dataIndex: "Company Permissions",
      width: 210,
      render: displayPermission,
    },
    {
      title: "Project Permissions",
      dataIndex: "Project Permissions",
      width: 210,
      render: displayPermission,
    },
    {
      title: "Status",
      dataIndex: "Status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {status || "Unknown"}
        </Tag>
      ),
    },
    ...(canManage
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 85,
            fixed: "right",
            render: (_, contact) =>
              canManageContact(accessLevel, contact) ? (
                <Dropdown
                  trigger={["click"]}
                  disabled={Boolean(busyAction) || loading || saving}
                  menu={{
                    items: [
                      {
                        key: "edit",
                        icon: <EditOutlined />,
                        label: "Edit Contact Info",
                      },
                      {
                        key: "invite",
                        icon: <SendOutlined />,
                        label:
                          contact.Status === "Not Invited"
                            ? "Send Invitation"
                            : "Resend Invitation",
                      },
                      ...(canDelete
                        ? [
                            {
                              key: "delete",
                              icon: <DeleteOutlined />,
                              label: "Delete Contact",
                              danger: true,
                            },
                          ]
                        : []),
                    ],
                    onClick: ({ key }) => {
                      if (key === "edit") openEdit(contact);
                      else if (key === "invite")
                        inviteContact(contact, company);
                      else if (key === "delete")
                        deleteContact(contact, company);
                    },
                  }}
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    loading={busyAction === getContactKey(contact, company)}
                    aria-label={`Actions for ${getContactName(contact)}`}
                  />
                </Dropdown>
              ) : (
                <span className={styles.secondary}>—</span>
              ),
          },
        ]
      : []),
  ];

  const bulkMenuItems = [
    { key: "edit", label: "Bulk Edit", icon: <EditOutlined /> },
    { key: "invite", label: "Bulk Invite", icon: <SendOutlined /> },
    ...(canDelete
      ? [
          {
            key: "delete",
            label: "Bulk Delete",
            icon: <DeleteOutlined />,
            danger: true,
          },
        ]
      : []),
  ];

  return (
    <>
      {messageContext}
      {modalContext}
      <main className={styles.page}>
        {loadError ? (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={<Button onClick={loadDirectory}>Retry</Button>}
          />
        ) : loading && !directory ? (
          <div style={{ padding: 64, textAlign: "center" }}>
            <Spin size="large" indicator={<LoadingComponent />} />
          </div>
        ) : accessLevel === 0 ? (
          <div className="d-flex flex-column">
            <div className="row w-100 d-flex justify-content-center p-5">
              <div className="d-flex flex-column align-items-center text-center gap-3">
                <Text>
                  You do not have the necessary permissions to access this page.
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div />
              <div className={styles.inlineGroup}>
                {companyGroups.length > 0 && syncButton()}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreate}
                  disabled={!canManage || loading || Boolean(busyAction)}
                >
                  Create Contact
                </Button>
              </div>
            </div>
            <div className={styles.toolbar}>
              <Input
                className={styles.search}
                prefix={<SearchOutlined />}
                placeholder="Search contacts or companies"
                aria-label="Search project directory"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
              />
              <div className={styles.inlineGroup}>
                {selectedContacts.length > 0 ? (
                  <>
                    <strong>{selectedContacts.length} selected</strong>
                    {hiddenSelectedCount > 0 && (
                      <span className={styles.secondary}>
                        ({hiddenSelectedCount} hidden by search)
                      </span>
                    )}
                    <Dropdown
                      trigger={["click"]}
                      disabled={loading || Boolean(busyAction) || saving}
                      menu={{
                        items: bulkMenuItems,
                        onClick: ({ key }) => {
                          if (key === "edit") openBulkEdit();
                          else if (key === "invite") bulkInvite();
                          else if (key === "delete") bulkDelete();
                        },
                      }}
                    >
                      <Button
                        loading={
                          busyAction === "bulk-invite" ||
                          busyAction === "bulk-delete"
                        }
                        disabled={loading || Boolean(busyAction) || saving}
                      >
                        Bulk Actions <DownOutlined />
                      </Button>
                    </Dropdown>
                    <Button
                      type="link"
                      onClick={() => setSelectedRowKeys([])}
                      disabled={loading || Boolean(busyAction) || saving}
                    >
                      Clear Selection
                    </Button>
                  </>
                ) : (
                  <span className={styles.secondary}>
                    {visibleCount} {visibleCount === 1 ? "contact" : "contacts"}{" "}
                    in {companyGroups.length}{" "}
                    {companyGroups.length === 1 ? "company" : "companies"}
                  </span>
                )}
                {companyGroups.length > 0 && !search.trim() && (
                  <Button
                    type="link"
                    onClick={() =>
                      setExpandedCompanies(
                        activeKeys.length === companyKeys.length
                          ? []
                          : companyKeys,
                      )
                    }
                  >
                    {activeKeys.length === companyKeys.length
                      ? "Collapse all"
                      : "Expand all"}
                  </Button>
                )}
              </div>
            </div>
            <Spin spinning={loading} indicator={<LoadingComponent />}>
              {companyGroups.length ? (
                <Collapse
                  className={styles.companies}
                  activeKey={activeKeys}
                  onChange={setExpandedCompanies}
                  items={companyGroups.map(({ company, contacts }) => ({
                    key: company,
                    label: (
                      <div className={styles.companyLabel}>
                        <strong>{company || "No company"}</strong>
                        <Badge
                          count={contacts.length}
                          showZero
                          className={styles.companyCount}
                        />
                      </div>
                    ),
                    children: (
                      <Table
                        rowKey={(contact) => getContactKey(contact, company)}
                        columns={columns(company)}
                        dataSource={contacts}
                        rowSelection={
                          canManage
                            ? rowSelection(company, contacts)
                            : undefined
                        }
                        size="middle"
                        scroll={{ x: canManage ? 1313 : 1228 }}
                        pagination={{
                          defaultPageSize: 10,
                          showSizeChanger: true,
                          hideOnSinglePage: true,
                        }}
                      />
                    ),
                  }))}
                />
              ) : (
                <Empty
                  className={styles.emptyState}
                  description={
                    <div className={styles.emptyDescription}>
                      <strong>
                        {search.trim()
                          ? "No contacts match your search"
                          : "No project contacts yet"}
                      </strong>
                      <span className={styles.secondary}>
                        {search.trim()
                          ? "Try a different search or sync the latest contacts from Procore."
                          : "Sync with Procore to bring contacts into this project directory."}
                      </span>
                    </div>
                  }
                >
                  <div className={styles.inlineGroup}>
                    {search.trim() && (
                      <Button onClick={() => setSearch("")}>
                        Clear Search
                      </Button>
                    )}
                    {syncButton(true)}
                  </div>
                </Empty>
              )}
            </Spin>
          </>
        )}
      </main>
      {dialog?.type === "bulk-edit" && canManage && (
        <BulkEditForm
          count={dialog.contacts.length}
          companies={companyOptions}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          permissionsError={permissionsError}
          saving={saving}
          error={formError}
          onRetryPermissions={loadPermissions}
          onCancel={closeDialog}
          onSubmit={submitBulkEdit}
        />
      )}
      {dialog && dialog.type !== "bulk-edit" && canManage && (
        <ContactForm
          editingContact={dialog.contact}
          saving={saving}
          error={formError}
          companies={companyOptions}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          permissionsError={permissionsError}
          onRetryPermissions={loadPermissions}
          onCancel={closeDialog}
          onSubmit={submitContact}
        />
      )}
    </>
  );
};

export default ProjectContacts;
